import { Bell, Check, ChevronDown, HelpCircle, LogOut, User, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/utils/cn';
import type { DashboardRole } from '@/shared/types/session';
import type { TenantSubtype } from '@propertyscan/shared/types';
import { resolveDashboardRoleId, resolveRoleLabel } from '@/shared/utils/dashboard-role';
import { useSignOut } from '@/modules/auth/hooks/use-sign-out';
import { useAddAccount, useOtherAccounts, useSwitchAccount, type SavedAccount } from '@/modules/accounts';
import { ADMIN_ROUTES, SETTINGS_ROUTES } from '@/api/constants/app.url';
import { NotificationDropdown } from '@/modules/notifications';
import { useNotificationsQuery } from '@/modules/notifications';
import { GlobalSearch } from '@/modules/search';
import { HelpPanel } from '@/shared/help';

interface Props {
    tenantName: string;
    userName:   string;
    userEmail:  string;
    role:       DashboardRole;
    subtype:    TenantSubtype | null;
    isDevMode:  boolean;
}

function initialsOf(name: string): string {
    return name
        .split(' ')
        .filter(Boolean)
        .map((s) => s[0]!)
        .join('')
        .slice(0, 2)
        .toUpperCase() || '?';
}

export function Topbar({ tenantName: _tenantName, userName, userEmail, role, subtype, isDevMode }: Props) {
    const signOut  = useSignOut();
    const navigate = useNavigate();
    // Other accounts signed in on this browser, and the two actions on them.
    const otherAccounts = useOtherAccounts();
    const switchAccount = useSwitchAccount();
    const addAccount    = useAddAccount();
    const roleId   = resolveDashboardRoleId(role, subtype);
    const roleLabel = resolveRoleLabel(role, subtype);
    const initials = initialsOf(userName || userEmail);

    const [userOpen,  setUserOpen]  = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [helpOpen,  setHelpOpen]  = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    /**
     * Super-admins get neither the bell nor the "?" — they have dedicated
     * Notifications and Help Controls pages in the sidebar, and the tenant feed
     * and help panel are both client-account surfaces that don't apply to them.
     */
    const isAdmin = roleId === 'admin';

    // Drives the unread badge; the dropdown reuses the same polling query. Off
    // for admins — the feed is tenant-scoped and they have no tenant.
    const { data: notifData } = useNotificationsQuery({ limit: 8 }, { enabled: !isAdmin });
    const unreadCount = notifData?.unread_count ?? 0;

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setUserOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const onProfile = () => {
        setUserOpen(false);
        // Super-admins have their own profile page; everyone else opens Settings → Profile.
        navigate(roleId === 'admin' ? ADMIN_ROUTES.PROFILE : SETTINGS_ROUTES.profile);
    };

    const onSignOut = () => {
        setUserOpen(false);
        signOut();
    };

    const onSignOutAll = () => {
        setUserOpen(false);
        signOut({ allAccounts: true });
    };

    const onAddAccount = () => {
        setUserOpen(false);
        addAccount();
    };

    const onSwitchAccount = (account: SavedAccount) => {
        setUserOpen(false);
        switchAccount(account);
    };

    return (
        <>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-ink-100 bg-white px-6">
            {/* Global search — fans out to properties / leads / QR codes */}
            <GlobalSearch />

            <div ref={menuRef} className="flex items-center gap-2">
                {isDevMode && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-900">
                        DEV · auth bypassed
                    </span>
                )}

                {/* Help — client accounts only */}
                {!isAdmin && (
                    <button
                        type="button"
                        aria-label="Help"
                        title="Help"
                        onClick={() => { setHelpOpen(true); setNotifOpen(false); setUserOpen(false); }}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-700 transition hover:bg-ink-50"
                    >
                        <HelpCircle className="h-4 w-4" />
                    </button>
                )}

                {/* Notifications — client accounts only */}
                {!isAdmin && (
                    <div className="relative">
                        <button
                            type="button"
                            aria-label="Notifications"
                            onClick={() => { setNotifOpen((v) => !v); setUserOpen(false); }}
                            className="relative flex h-9 w-9 items-center justify-center rounded-lg text-ink-700 transition hover:bg-ink-50"
                        >
                            <Bell className="h-4 w-4" />
                            {unreadCount > 0 && (
                                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-sky px-1 font-ui text-[10px] font-bold text-white ring-2 ring-white">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                        {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} />}
                    </div>
                )}

                {!isAdmin && <div className="mx-1 h-6 w-px bg-ink-100" />}

                {/* User menu */}
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => { setUserOpen((v) => !v); setNotifOpen(false); }}
                        className="flex h-10 items-center gap-2.5 rounded-lg px-2 pr-3 transition hover:bg-ink-50"
                    >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-navy to-brand-sky font-display text-xs font-bold text-white">
                            {initials}
                        </div>
                        <div className="hidden text-left md:block">
                            <div className="text-xs font-semibold leading-tight text-brand-navy">{userName}</div>
                            <div className="font-ui text-[10px] leading-tight text-ink-500">{roleLabel}</div>
                        </div>
                        <ChevronDown className="h-3.5 w-3.5 text-ink-500" />
                    </button>

                    {userOpen && (
                        <div className="absolute right-0 top-full mt-2 w-72 overflow-hidden rounded-xl2 border border-ink-100 bg-white shadow-pop">
                            <div className="border-b border-ink-100 p-3">
                                <div className="flex items-center gap-1.5">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-semibold text-brand-navy">{userName}</div>
                                        <div className="truncate text-xs text-ink-500">{userEmail}</div>
                                    </div>
                                    {otherAccounts.length > 0 && (
                                        <Check className="ml-auto h-4 w-4 shrink-0 text-brand-sky-500" aria-label="Active account" />
                                    )}
                                </div>
                                <div className="mt-1 inline-flex items-center rounded-full bg-brand-sky-50 px-2 py-0.5 font-ui text-[10px] font-bold uppercase tracking-wider text-brand-sky-500">
                                    {roleLabel}
                                </div>
                            </div>
                            {/* Other accounts signed in on this browser. One is
                                active at a time — picking one swaps the session
                                and reloads; nobody is signed out. */}
                            {otherAccounts.length > 0 && (
                                <div className="border-b border-ink-100 p-2">
                                    <div className="px-3 pb-1 pt-1 font-ui text-[10px] font-semibold uppercase tracking-wider text-ink-300">
                                        Switch account
                                    </div>
                                    {otherAccounts.map((a) => (
                                        <button
                                            key={a.session.userId}
                                            type="button"
                                            onClick={() => onSwitchAccount(a)}
                                            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition hover:bg-ink-50"
                                        >
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink-100 font-display text-[10px] font-bold text-brand-navy">
                                                {initialsOf(a.session.fullName || a.session.email)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-xs font-semibold text-brand-navy">
                                                    {a.session.fullName || a.session.email}
                                                </div>
                                                <div className="truncate font-ui text-[10px] text-ink-500">
                                                    {a.session.tenantName}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="p-2">
                                <button
                                    type="button"
                                    onClick={onProfile}
                                    className={cn(
                                        'inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-navy',
                                        'hover:bg-ink-50',
                                    )}
                                >
                                    <User className="h-3.5 w-3.5" /> Profile
                                </button>
                                <button
                                    type="button"
                                    onClick={onAddAccount}
                                    className="inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-navy hover:bg-ink-50"
                                >
                                    <UserPlus className="h-3.5 w-3.5" /> Add account
                                </button>
                                <button
                                    type="button"
                                    onClick={onSignOut}
                                    className="inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                    <LogOut className="h-3.5 w-3.5" />
                                    {otherAccounts.length > 0 ? `Sign out of ${userEmail}` : 'Sign out'}
                                </button>
                                {/* Only meaningful with more than one signed in —
                                    otherwise it is the button above. */}
                                {otherAccounts.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={onSignOutAll}
                                        className="inline-flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-ink-500 hover:bg-ink-50"
                                    >
                                        Sign out of all accounts
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>

        {/* Outside the sticky header: `position: sticky` + z-index creates a
            stacking context, which would trap the overlay inside it. */}
        {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
        </>
    );
}
